import React, { useRef } from 'react';
import { Icon, Spinner, Stack } from '@servicetitan/design-system';
import useUserStatus from '@modules/UserStatusModule/hooks/useUserStatus';
import useOnClickOutside from '@lib/hooks/useOnClickOutside';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import UserStatusModule from '@modules/UserStatusModule';
import UserStatusDisplay from './UserStatusDisplay';
import useCountdownTimer from '@lib/hooks/useCountDownTimer';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import statusValues, {
  StatusBackgroundColor,
  StatusDotColor,
} from '@modules/UserStatusModule/statusValues';
import classNames from 'classnames';
import styles from './UserStatusMenu.module.css';
import StatusLight from '@components/StatusLight/StatusLight';
import { mainLayoutLocators } from '@locators/main-layout';

/** Displays and updates the status of the current user via a dropdown like menu */
export function UserStatusMenu() {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const {
    status: userStatus,
    loading,
    error,
    canChange,
    wrapUpTimeEndsAt,
  } = useUserStatus();
  const disabled = loading || !!error || !canChange;
  const clickOutsideRef = useRef<HTMLDivElement>(null);
  const clickOutsideIgnoreRef = useRef<HTMLButtonElement>(null);
  useOnClickOutside(
    clickOutsideRef,
    () => setOpen(false),
    clickOutsideIgnoreRef,
  );

  const { count } = useCountdownTimer(wrapUpTimeEndsAt ?? 0);

  // set the status as wrap up if still in automated queue status (aka Recovery)
  const status = count > 0 ? 'WRAP_UP' : userStatus;

  // we don't want to show the automated queue wrap up status (aka Recovery) in the dropdown
  const options = Object.entries(statusValues)
    .filter(([value]) => value !== 'WRAP_UP')
    .map(([value, text]) => ({
      text,
      value,
    }));

  return (
    <div className='position-relative'>
      <button
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
        }}
        id={mainLayoutLocators.navBar.userStatusMenu}
        ref={clickOutsideIgnoreRef}
        className={classNames(
          styles.userStatusDropdownButton,
          'border-radius-pill',
          { [styles.userStatusDropdownButtonDisabled]: disabled },
        )}
        style={
          {
            '--status-background-color': StatusBackgroundColor[status],
          } as React.CSSProperties
        }
      >
        <Stack justifyContent='space-around' alignItems='center'>
          <Stack.Item fill>
            <Stack alignItems='center'>
              <Icon
                className='m-r-half'
                name='shape_circle'
                color={StatusDotColor[status]}
                el='span'
                size={14}
              />
              <UserStatusDisplay status={status} wrapUpTimeInSeconds={count} />
            </Stack>
          </Stack.Item>
          {/* display expand icons if not loading */}
          {loading ? (
            <Spinner
              color={StatusDotColor[status]}
              el='span'
              className={styles.spinner}
            />
          ) : open ? (
            <Icon name='expand_less' color={StatusDotColor[status]} el='span' />
          ) : (
            <Icon name='expand_more' color={StatusDotColor[status]} el='span' />
          )}
        </Stack>
      </button>
      <div
        className={classNames(
          styles.userStatusDropdownMenu,
          open ? 'd-f' : 'd-n',
        )}
        ref={clickOutsideRef}
      >
        <Stack
          direction='column'
          spacing={1}
          alignItems='flex-start'
          justifyContent='center'
        >
          {options.map((option) => (
            <StatusLight
              id={mainLayoutLocators.navBar.userStatusMenuOption}
              key={option.value}
              onClick={() => {
                UserStatusModule.setUserStatus(
                  user?.id || '',
                  option.value as UserSetAvailabilityStatus,
                );
                setOpen(false);
              }}
              color={StatusDotColor[option.value]}
              className={styles.userStatusDropdownMenuItem}
              disabled={disabled}
            >
              {option.text}
            </StatusLight>
          ))}
        </Stack>
      </div>
    </div>
  );
}
